import { useEffect, useRef } from 'react'
import { Plus, Trash2 } from 'lucide-react'

import { Button } from '../../ui/Button'
import { Input } from '../../ui/Field'
import { GUIDED_VOLUME_BOUNDS, type GuidedVolume } from '../../core/guided'
import { VOLUME_NOTE } from '../../core/popularImages'

/** What a new volume starts as: the conventional data path and a small size the tenant changes. */
const NEW_VOLUME: GuidedVolume = { mount: '/data', sizeGi: 10 }

/**
 * The Persistent volume card body. One optional volume, mounted into the
 * container. Without one, the only storage is the Size card's, which is lost
 * when the instance is replaced.
 */
export function VolumeSection({ volume, unavailable, remedy, onChooseCustom, errors, submitted, onChange }: { volume: GuidedVolume | undefined; unavailable: string | null; remedy?: string | undefined; onChooseCustom?: (() => void) | undefined; errors: Record<string, string>; submitted: boolean; onChange: (volume: GuidedVolume | undefined) => void }) {
  // The id to focus once the next render has put it in the document.
  const pendingFocus = useRef<string | null>(null)
  useEffect(() => {
    if (!pendingFocus.current) return
    document.getElementById(pendingFocus.current)?.focus()
    pendingFocus.current = null
  })

  // This rental cannot carry a volume (`volumeUnavailableReason`): say why instead of offering one.
  if (!volume && unavailable) {
    return <div id="volume-unavailable" role="note">
      <p className="field-help">{remedy ? `${unavailable} ${remedy}` : unavailable}</p>
      {onChooseCustom && <Button type="button" variant="link" onClick={onChooseCustom}>Rent custom capacity on this host instead</Button>}
    </div>
  }

  if (!volume) {
    return <>
      <p className="field-help">Without a volume, data on the instance disk is lost if the instance is replaced.</p>
      <div className="row-list-actions">
        <Button id="volume-add" type="button" variant="secondary" size="sm" onClick={() => { pendingFocus.current = 'volume-mount'; onChange({ ...NEW_VOLUME }) }}><Plus aria-hidden="true" size={16} />Add persistent volume</Button>
      </div>
    </>
  }

  return <>
    <p className="field-help" id="volume-note">{VOLUME_NOTE}</p>
    <div className="volume-row" role="group" aria-label="Persistent volume" aria-describedby="volume-note">
      <Input id="volume-mount" label="Mount path" type="text" autoComplete="off" spellCheck={false} value={volume.mount} error={submitted ? errors['volume-mount'] : undefined} onChange={(event) => onChange({ ...volume, mount: event.target.value })} />
      {/* A text field, not type="number", for the same reason as the port rows. */}
      <Input id="volume-size" label="Size (GB)" type="text" inputMode="numeric" pattern="[0-9]*" maxLength={4} autoComplete="off" description={`${GUIDED_VOLUME_BOUNDS.min} to ${GUIDED_VOLUME_BOUNDS.max} GB`} value={volume.sizeGi || ''} error={submitted ? errors['volume-size'] : undefined} onChange={(event) => { const digits = event.target.value.replace(/\D/g, ''); onChange({ ...volume, sizeGi: digits === '' ? 0 : Number(digits) }) }} />
      <Button type="button" variant="secondary" size="sm" className="volume-remove" onClick={() => { pendingFocus.current = 'volume-add'; onChange(undefined) }}><Trash2 aria-hidden="true" size={16} />Remove volume</Button>
    </div>
  </>
}
